/* eslint-disable @typescript-eslint/no-unused-vars */
import { Api } from 'src/apis/api';
import { ICreditCardInfo } from 'src/controllers/pages/CheckoutController';

import TrackingService from '../../../elements/src/tracking/TrackingService';
import { GatewayService } from '../services/GatewayService';
import { ISiftEvent } from '../types/sift';

import { getUrlParameters } from './url';
import { getAppUtms } from './utms';

// Import Errors from the correct location if needed for error reporting
// Using console.error for now to avoid import errors

/**
 * Utility class for sending Sift Science events
 * https://sift.com/developers/docs/curl/events-api
 */
export class SiftEvents {
  private static encodeUserId(userId: string): string {
    if (!userId) return '';

    // If it looks like an email, encode it
    if (userId.includes('@') && userId.includes('.')) {
      return Buffer.from(userId).toString('base64');
    }

    return userId;
  }

  private static addCustomParams(eventData: any, orderData?: any): void {
    if (eventData && orderData) {
      const auid = orderData.custom_order_auid;
      auid ? (eventData.crm_auid = auid) : null;
      const paySource = orderData.paySource;
      paySource ? (eventData.crm_paySource = paySource) : null;
      const merchantId = orderData.merchantId || orderData?.forceMerchantId;
      merchantId ? (eventData.crm_merchantId = merchantId) : null;
      const fullUrl =
        orderData.fullUrl ||
        orderData?.customFields?.http_referer ||
        orderData?.custom3;
      fullUrl ? (eventData.crm_fullUrl = fullUrl) : null;
      const sessionId = orderData.sessionId || orderData.custom_order_sid;
      sessionId ? (eventData.crm_session_id_no_ML = sessionId) : null;
    }
  }

  private static addAddressDetails(
    eventData: any,
    responseData?: any,
    orderData?: any,
  ): void {
    if ((eventData && orderData) || (eventData && responseData)) {
      eventData.$billing_address = {};
      const name = responseData?.name;
      name ? (eventData.$billing_address.$name = name) : null;
      const address1 = responseData?.address1 || orderData?.address1;
      address1 ? (eventData.$billing_address.$address_1 = address1) : null;
      const address2 = responseData?.address2 || orderData?.address2;
      address2 ? (eventData.$billing_address.$address_2 = address2) : null;
      const city = responseData?.city || orderData?.city;
      city ? (eventData.$billing_address.$city = city) : null;
      const state = responseData?.state || orderData?.state;
      state ? (eventData.$billing_address.$region = state) : null;
      const country = responseData?.country || orderData?.country;
      country ? (eventData.$billing_address.$country = country) : null;
      const postalCode = responseData?.postalCode || orderData?.postalCode;
      postalCode ? (eventData.$billing_address.$zipcode = postalCode) : null;
      const phoneNumber = responseData?.phoneNumber || orderData?.phoneNumber;
      phoneNumber ? (eventData.$billing_address.$phone = phoneNumber) : null;

      eventData.$shipping_address = {};
      name ? (eventData.$shipping_address.$name = name) : null;
      address1 ? (eventData.$shipping_address.$address_1 = address1) : null;
      address2 ? (eventData.$shipping_address.$address_2 = address2) : null;
      city ? (eventData.$shipping_address.$city = city) : null;
      state ? (eventData.$shipping_address.$region = state) : null;
      country ? (eventData.$shipping_address.$country = country) : null;
      postalCode ? (eventData.$shipping_address.$zipcode = postalCode) : null;
      phoneNumber ? (eventData.$shipping_address.$phone = phoneNumber) : null;
    }
  }

  // private static async getGatewayFromMerchantId(merchantId: string | number, orderId?: string): Promise<string> {
  //   if (!merchantId) return `1. merchantId - ${merchantId}, not_found`;

  //   try {
  //     const gatewayInfo = await GatewayService.getInstance().getGatewayInfo(merchantId, orderId);

  //     if (gatewayInfo) {
  //       return this.mapGatewayNameToSift(gatewayInfo.gatewayName);
  //     }

  //     return `2. merchantId - ${merchantId}, not_found (API returned: ${JSON.stringify(gatewayInfo)})`;
  //   } catch (error) {
  //     return `3. merchantId - ${merchantId}, not_found`;
  //   }
  // }

  private static async getGatewayFromMerchantId(
    merchantId: string | number,
    orderId?: string,
  ): Promise<string> {
    if (!merchantId) return `1. merchantId - ${merchantId}, not_found`;

    // Hardcoded fallback mapping for all known merchant IDs
    const knownGateways: Record<string, string> = {
      '1': '$stripe',
      '2': '$paypal',
      '3': '$paypal',
      '4': '$paypal',
      '5': '$reach',
      '6': '$stripe',
      '7': '$stripe',
      '8': '$fluidpay',
      '9': '$other',
      '10': '$paypal',
      '11': '$stripe',
      '12': '$paypal',
      '13': '$paypal',
      '14': '$paypal',
      '15': '$paypal',
      '16': '$stripe',
      '17': '$stripe',
      '18': '$stripe',
      '19': '$stripe',
      '20': '$paypal',
      '21': '$stripe',
      '22': '$stripe',
      '23': '$paypal',
      '24': '$stripe',
      '25': '$stripe',
      '26': '$stripe',
      '27': '$stripe',
      '28': '$paypal',
      '29': '$other',
      '30': '$other',
      '31': '$nmi',
      '32': '$stripe',
      '33': '$nmi',
      '34': '$nmi',
      '35': '$nmi',
      '36': '$nmi',
      '37': '$nmi',
      '38': '$nmi',
      '39': '$nmi',
      '40': '$stripe',
      '41': '$nmi',
      '42': '$nmi',
      '43': '$nmi',
      '44': '$nmi',
      '45': '$paypal',
      '46': '$nmi',
      '47': '$nmi',
      '48': '$nmi',
      '49': '$paypal',
      '50': '$nmi',
      '51': '$nmi',
      '52': '$nmi',
      '53': '$nmi',
      '54': '$nmi',
      '55': '$nmi',
      '56': '$nmi',
      '57': '$nmi',
      '58': '$paypal',
      '59': '$nmi',
      '60': '$other',
      '61': '$other',
      '62': '$stripe',
      '63': '$paypal',
      '64': '$paypal',
      '65': '$paypal',
      '66': '$paypal',
      '67': '$paypal',
      '68': '$paypal',
      '69': '$paypal',
      '70': '$nmi',
      '71': '$nmi',
      '72': '$paypal',
      '73': '$paypal',
      '74': '$paypal',
      '75': '$nmi',
      '76': '$nmi',
      '77': '$nmi',
      '78': '$nmi',
      '79': '$nmi',
      '80': '$nmi',
      '81': '$nmi',
      '82': '$nmi',
      '83': '$nmi',
      '84': '$other',
      '85': '$braintree',
      '86': '$braintree',
      '87': '$nmi',
      '88': '$nmi',
      '89': '$nmi',
      '90': '$nmi',
      '91': '$nmi',
      '92': '$nmi',
      '93': '$nmi',
      '94': '$nmi',
      '95': '$nmi',
      '96': '$nmi',
      '97': '$nmi',
      '98': '$nmi',
      '99': '$nmi',
      '100': '$nmi',
      '101': '$nmi',
      '102': '$nmi',
      '103': '$nmi',
      '104': '$nmi',
      '105': '$nmi',
      '106': '$paypal',
      '107': '$stripe',
      '108': '$braintree',
      '109': '$paypal',
      '110': '$nmi',
      '111': '$nmi',
      '112': '$braintree',
      '113': '$braintree',
      '114': '$nmi',
      '115': '$nmi',
      '116': '$nmi',
      '117': '$stripe',
      '118': '$braintree',
      '119': '$braintree',
      '120': '$paypal',
      '121': '$paypal',
      '122': '$paypal',
      '123': '$nmi',
      '124': '$other',
      '125': '$nmi',
      '126': '$other',
      '127': '$nmi',
      '128': '$nmi',
      '129': '$nmi',
      '130': '$nmi',
      '131': '$other',
      '132': '$other',
      '133': '$other',
      '134': '$reach',
      '135': '$reach',
      '136': '$reach',
      '137': '$reach',
      '138': '$reach',
      '139': '$nmi',
      '140': '$nmi',
      '141': '$braintree',
      '142': '$braintree',
      '143': '$braintree',
      '144': '$braintree',
      '145': '$nmi',
      '146': '$nmi',
      '147': '$stripe',
      '148': '$other',
      '149': '$nmi',
      '150': '$stripe',
      '151': '$stripe',
      '152': '$stripe',
      '153': '$stripe',
      '154': '$stripe',
      '155': '$stripe',
      '156': '$stripe',
      '157': '$stripe',
      '158': '$stripe',
      '159': '$stripe',
      '160': '$stripe',
      '161': '$stripe',
      '162': '$stripe',
      '163': '$stripe',
      '164': '$stripe',
      '165': '$stripe',
      '166': '$stripe',
      '167': '$stripe',
      '168': '$stripe',
      '169': '$braintree',
      '170': '$braintree',
      '171': '$braintree',
      '172': '$stripe',
      '173': '$stripe',
      '174': '$stripe',
      '175': '$stripe',
      '176': '$stripe',
      '177': '$stripe',
      '178': '$checkoutcom',
      '179': '$checkoutcom',
      '180': '$checkoutcom',
      '181': '$checkoutcom',
      '182': '$checkoutcom',
      '183': '$checkoutcom',
      '184': '$checkoutcom',
      '185': '$checkoutcom',
      '186': '$checkoutcom',
      '187': '$checkoutcom',
      '188': '$checkoutcom',
      '189': '$checkoutcom',
      '190': '$checkoutcom',
      '191': '$checkoutcom',
      '192': '$braintree',
      '193': '$braintree',
      '194': '$checkoutcom',
      '195': '$checkoutcom',
      '196': '$stripe',
      '197': '$fluidpay',
      '198': '$fluidpay',
      '199': '$stripe',
      '200': '$stripe',
      '201': '$stripe',
      '202': '$fluidpay',
      '203': '$paypal',
      '204': '$stripe',
      '205': '$stripe',
      '206': '$stripe',
      '207': '$paypal',
      '208': '$nmi',
      '209': '$nmi',
      '210': '$stripe',
      '211': '$braintree',
      '212': '$stripe',
      '213': '$reach',
      '214': '$reach',
      '215': '$braintree',
      '216': '$paypal',
      '217': '$paypal',
      '218': '$stripe',
      '219': '$paypal',
      '220': '$paypal',
      '221': '$other',
      '222': '$stripe',
      '223': '$stripe',
      '224': '$stripe',
      '225': '$stripe',
      '226': '$stripe',
      '227': '$braintree',
      '228': '$braintree',
      '232': '$stripe',
      '233': '$braintree',
      '234': '$nmi',
      '235': '$stripe',
      '236': '$braintree',
      '237': '$stripe',
    };

    // Convert merchantId to string for consistent comparison
    const merchantIdStr = String(merchantId).trim();

    try {
      // Try the normal API flow first
      const gatewayInfo = await GatewayService.getInstance().getGatewayInfo(
        merchantId,
        orderId,
      );

      if (gatewayInfo?.gatewayName) {
        return this.mapGatewayNameToSift(gatewayInfo.gatewayName);
      }

      // If API flow fails, use hardcoded mapping
      if (knownGateways[merchantIdStr]) {
        if (window.DEBUG) {
          console.warn(
            `[SIFT FALLBACK] Using hardcoded gateway mapping for merchantId ${merchantId}: ${knownGateways[merchantIdStr]}`,
          );
        }
        return knownGateways[merchantIdStr];
      }

      return `2. merchantId - ${merchantId}, not_found`;
    } catch (error) {
      // Even in error case, try the hardcoded mapping
      if (knownGateways[merchantIdStr]) {
        if (window.DEBUG) {
          console.warn(
            `[SIFT FALLBACK] Using hardcoded gateway mapping after error for merchantId ${merchantId}: ${knownGateways[merchantIdStr]}`,
          );
        }
        return knownGateways[merchantIdStr];
      }
      return `3. merchantId - ${merchantId}, not_found`;
    }
  }

  private static mapGatewayNameToSift(gatewayName: string): string {
    if (!gatewayName) return `1. gatewayName - ${gatewayName}, not_found`;

    const name = gatewayName.toLowerCase();

    const gatewayKeywords = [
      { keywords: ['stripe'], siftName: '$stripe' },
      { keywords: ['paypal', 'pay pal'], siftName: '$paypal' },
      { keywords: ['braintree', 'brain tree'], siftName: '$braintree' },
      { keywords: ['checkout.com', 'checkout'], siftName: '$checkoutcom' },
      { keywords: ['fluid', 'fluidpay'], siftName: '$fluidpay' },
      { keywords: ['nmi'], siftName: '$nmi' },
      { keywords: ['reach'], siftName: '$reach' },
      { keywords: ['ccbill'], siftName: '$reach' },
    ];

    // Find the first matching keyword
    for (const gateway of gatewayKeywords) {
      if (gateway.keywords.some(keyword => name.includes(keyword))) {
        if (window.DEBUG) {
          console.log(
            `[Sift Debug] Mapped gateway name '${gatewayName}' to '${gateway.siftName}' via keyword matching`,
          );
        }
        return gateway.siftName;
      }
    }

    // Default fallback
    if (window.DEBUG) {
      console.log(
        `[Sift Debug] Could not map gateway name '${gatewayName}' to a known Sift gateway`,
      );
    }
    return `2. gatewayName - ${gatewayName}, not_found`;
  }

  private static mapPaymentType(paySource: string): string {
    if (!paySource) return '$digital_wallet';

    // If it's already in Sift format (starts with $), return it as is
    if (paySource.startsWith('$')) {
      return paySource;
    }

    // Convert to uppercase for case-insensitive matching
    const source = paySource.toUpperCase();

    // Map payment types according to Sift's documentation
    const paymentMap: Record<string, string> = {
      CREDITCARD: '$credit_card',
      CREDIT_CARD: '$credit_card',
      PAYPAL: '$electronic_fund_transfer',
      KLARNA: '$digital_wallet',
      APPLEPAY: '$digital_wallet',
      APPLE_PAY: '$digital_wallet',
      GOOGLEPAY: '$digital_wallet',
      GOOGLE_PAY: '$digital_wallet',
      PREPAID: '$digital_wallet',
    };

    return paymentMap[source] || '$digital_wallet';
  }

  private static convertToIntegerMicros(price: string | number): number {
    // Convert to number if it's a string
    const numericPrice = typeof price === 'string' ? Number(price) : price;

    // Ensure it's a valid number
    if (isNaN(numericPrice)) return 0;

    // Convert to micros and ensure it's an integer
    return Math.round(numericPrice * 1000000);
  }

  private static getBrowserInfo(): Record<string, string> {
    // Check if we're running in a browser environment
    if (typeof window === 'undefined' || !window.navigator) {
      return {};
    }

    return {
      $user_agent: window.navigator.userAgent || '',
      $accept_language: window.navigator.language || '',
      $content_language: window.navigator.language || '',
    };
  }

  static async safelyTrackEvent(
    eventType: string,
    contextName: string,
    createEventFn: () => ISiftEvent | Promise<ISiftEvent> | any,
    debugData?: Record<string, any>,
    returnScore: boolean = false,
  ): Promise<void> {
    try {
      // Log attempt to create event
      if (window.DEBUG)
        console.log(`[Sift Debug] Attempting to create ${eventType} event`);

      // Call the event creation function (might be async)
      const event = await Promise.resolve(createEventFn());

      event.$time = Date.now();
      event.crm_version_no_ML = window?.FLAGS?.controller_v;

      if (window.GEO?.ip && event) {
        event.$ip = window.GEO?.ip;
      }

      // Log event creation result
      if (window.DEBUG) {
        if (!event) {
          console.log(
            `[Sift Debug] No event created for ${eventType} - returned null`,
          );
        } else {
          console.log(
            `[Sift Debug] Successfully created ${eventType} event: ` +
              JSON.stringify(event, null, 2),
          );
        }
      }

      // Only proceed if we have a valid event
      if (event) {
        const emailAddress =
          debugData?.lead?.emailAddress ||
          debugData?.emailAddress ||
          debugData?.custom_order_auid ||
          debugData?.customFields?.auid;

        if (!event.$user_id && emailAddress) {
          event.$user_id = emailAddress;
        }

        // Always encode the user_id if it's present (handles email addresses)
        if (event.$user_id) {
          event.$user_id = this.encodeUserId(event.$user_id);
        }

        // Add browser information to all events
        if (
          !`${eventType}`.toLocaleLowerCase().includes('link_session_to_user')
        ) {
          event.$brand_name = window.BRAND;
          event.$browser = this.getBrowserInfo();
        }

        // Add debug logging for the return_score flag
        if (returnScore && window.DEBUG) {
          console.log(`[Sift Debug] Requesting score for ${event.$type} event`);
          returnScore = false;
        }

        if (
          returnScore &&
          `${emailAddress}`.toLocaleLowerCase().includes('qa+')
        ) {
          returnScore = false;
        }

        if (
          returnScore &&
          `${getUrlParameters('utm_source')}`?.toLocaleLowerCase() === 'test'
        ) {
          returnScore = false;
        }

        // Use the existing TrackingService infrastructure
        TrackingService.trackEvent('sift', event.$type, {
          siftEvent: event, // Nest the Sift event in a dedicated field
          return_score: returnScore, // Add the return_score flag if requested
        });

        if (window.DEBUG) {
          console.log(`[Sift Debug] ${contextName} event sent`, {
            ...(debugData || {}),
            user_id: event.$user_id,
            event_type: event.$type,
          });
        }
      }
    } catch (error) {
      // Use console.error instead of Errors.report for debugging
      console.error(`[Sift Error] Error in ${contextName} event:`, error, {
        eventType: `sift_${contextName}`,
        ...debugData,
      });
    }
  }

  static async createOrder(
    orderData: any,
    options?: {
      paymentType?: string;
      paymentGateway?: string;
      returnScore?: boolean;
      isExpressPayment?: boolean;
      cardInfo?: ICreditCardInfo;
    },
  ): Promise<void> {
    await this.safelyTrackEvent(
      '$create_order',
      'create_order',
      async () => {
        if (!orderData) return null;

        const orderId = orderData.orderId;
        const sessionId =
          orderData.custom_order_auid ||
          orderData.sessionId ||
          orderData.custom_order_sid;
        const userId = orderData.emailAddress;

        // Skip orderId check for express payments, but still require user/session ID
        if (
          (!orderId && !options?.isExpressPayment) ||
          !(userId || sessionId)
        ) {
          if (window.DEBUG) {
            console.log(`[Sift Debug] Order validation failed:`, {
              hasOrderId: !!orderId,
              isExpress: !!options?.isExpressPayment,
              hasUserOrSessionId: !!(userId || sessionId),
            });
          }
          return null;
        }

        // Extract products from orderData
        const products = [];
        let i = 1;
        while (orderData[`product${i}_id`]) {
          products.push({
            id: orderData[`product${i}_id`],
            name: orderData[`product${i}_name`] || `Product ${i}`,
            price: this.convertToIntegerMicros(orderData[`product${i}_price`]),
            quantity: 1,
            currency_code: orderData.currency_code || 'USD',
          });
          i++;
        }

        if (products.length === 0) return null;

        // Calculate total amount
        const totalAmount = products.reduce(
          (sum, product) => sum + product.price,
          0,
        );

        // Build the Sift event
        const siftEvent: ISiftEvent = {
          $type: '$create_order',
          $session_id: sessionId,
          // Only include orderId if it exists
          ...(orderId ? { $order_id: orderId } : {}),
          $amount: totalAmount,
          $currency_code: products?.[0]?.currency_code || 'USD',
          // Add is_express flag for express payments
          ...(options?.isExpressPayment ? { is_express_no_ML: true } : {}),
          $items: products.map(p => ({
            $item_id: String(p.id),
            $product_title: p.name,
            $price: p.price,
            $quantity: 1,
            $currency_code: p.currency_code,
          })),
          $payment_methods: [],
        };

        if (orderData?.address1) {
          this.addAddressDetails(siftEvent, null, orderData);
        }

        if (orderData?.emailAddress) {
          siftEvent.$user_email = orderData.emailAddress;
        }
        if (userId) {
          siftEvent.$user_id = userId;
        }
        this.addCustomParams(siftEvent, orderData);

        // Always apply the mapping - don't use raw value even if provided in options
        const rawPaymentType =
          orderData.paySource ||
          orderData.custom_order_paymentMethod ||
          options?.paymentType;

        // Map the payment type regardless of source to ensure consistent formatting
        const paymentType = this.mapPaymentType(rawPaymentType);

        if (window.DEBUG) {
          console.log(
            `[Sift Debug] Payment type mapping: ${
              rawPaymentType || 'unknown'
            } → ${paymentType}`,
          );
        }

        const merchantId = orderData?.forceMerchantId || orderData?.merchantId;

        // Use provided gateway option first, or look up from merchant ID
        let paymentGateway = options?.paymentGateway;

        // If no gateway provided in options, look it up from merchant ID
        if (!paymentGateway && merchantId) {
          paymentGateway = await this.getGatewayFromMerchantId(
            merchantId,
            orderId,
          );
        }

        if (window.DEBUG) {
          console.log(
            `[Sift Debug] Payment gateway: ${
              options?.paymentGateway
                ? `"${paymentGateway}" (provided in options)`
                : `"${paymentGateway}" from merchantId ${
                    merchantId || 'not provided'
                  }`
            }`,
          );
        }

        const paymentMethod: any = {
          $payment_type: paymentType,
          $payment_gateway: paymentGateway,
        };

        // Add card details if this is a credit card payment
        if (paymentType === '$credit_card') {
          const cardNumber = `${options?.cardInfo?.number}`;
          const cardBin = cardNumber.substring(0, 8);
          const cardLast4 = cardNumber.substring(
            cardNumber.length - 4,
            cardNumber.length,
          );
          if (window.DEBUG) {
            console.log(`[Sift Debug] Card: "${cardBin}", "${cardLast4}"`);
          }
          if (cardBin) {
            paymentMethod.$card_bin = cardBin;
          }
          if (cardLast4) {
            paymentMethod.$card_last4 = cardLast4;
          }
        }
        siftEvent.$payment_methods?.push(paymentMethod);

        if (window.DEBUG) {
          console.log(`[Sift Debug] Create order payment info:`, {
            rawPaymentType,
            mappedPaymentType: paymentType,
            merchantId,
            paymentGateway,
          });
        }

        return siftEvent;
      },
      { orderData },
      options?.returnScore,
    );
  }

  static async trackTransaction(
    orderData: any,
    responseData: any,
    options?: {
      paymentType?: string;
      paymentGateway?: string;
      returnScore?: boolean;
      isExpressPayment?: boolean;
      cardInfo?: ICreditCardInfo;
    },
    skipSessionLink = false,
  ): Promise<void> {
    if (window.DEBUG) {
      console.log(
        `[Sift Debug] trackTransaction called with: ` +
          JSON.stringify(
            {
              orderData,
              responseData,
              options,
            },
            null,
            2,
          ),
      );
    }

    await this.safelyTrackEvent(
      '$transaction',
      'transaction',
      async () => {
        if (!orderData && !responseData) {
          if (window.DEBUG)
            console.log(
              `[Sift Debug] trackTransaction aborted - missing orderData and responseData`,
            );
          return null;
        }

        // Use either the response orderId or the original one
        const orderId = responseData?.orderId || orderData?.orderId;
        if (window.DEBUG) {
          console.log(`[Sift Debug] orderId: `, orderId);
        }
        if (orderId && !responseData?.orderId) {
          const queryOrderData: any = await Api.queryOrder(orderId);
          if (window.DEBUG) {
            console.log(`[Sift Debug] queryOrderData: `, {
              queryOrderData: JSON.stringify(queryOrderData, null, 2),
            });
          }
          orderData = queryOrderData;
        }

        const sessionId =
          orderData?.custom_order_auid ||
          orderData?.sessionId ||
          orderData?.custom_order_sid;

        // Extract products and calculate total amount
        const products = [];
        let totalAmount = 0;
        let i = 1;

        while (orderData[`product${i}_id`]) {
          const price = parseFloat(`${orderData[`product${i}_price`]}`) || 0;
          // const qty = parseInt(`${orderData[`product${i}_qty`]}`, 10) || 1;

          products.push({
            id: orderData[`product${i}_id`],
            price: this.convertToIntegerMicros(price),
            quantity: 1,
          });

          totalAmount += price;
          i++;
        }
        if (window.DEBUG) {
          console.log(
            `[Sift Debug] products: ` + JSON.stringify({ products }, null, 2),
          );
        }
        // if (products.length === 0) return null;

        // Always apply the mapping - don't use raw value even if provided in options
        const rawPaymentType =
          orderData?.paySource ||
          orderData?.custom_order_paymentMethod ||
          options?.paymentType;

        // Map the payment type regardless of source to ensure consistent formatting
        const paymentType = this.mapPaymentType(rawPaymentType);

        if (window.DEBUG) {
          console.log(
            `[Sift Debug] Payment type mapping: ${
              rawPaymentType || 'unknown'
            } → ${paymentType}`,
          );
        }

        // Get merchant ID from order data or response
        const merchantId = responseData?.items?.[0]?.merchantId;

        // Use provided gateway option first, or look up from merchant ID
        let paymentGateway = options?.paymentGateway;

        // If no gateway provided in options, look it up from merchant ID
        if (!paymentGateway && merchantId) {
          paymentGateway = await this.getGatewayFromMerchantId(
            merchantId,
            orderId,
          );
        }

        if (window.DEBUG) {
          console.log(
            `[Sift Debug] Payment gateway: ${
              options?.paymentGateway
                ? `"${paymentGateway}" (provided in options)`
                : `"${paymentGateway}" from merchantId ${
                    merchantId || 'not provided'
                  }`
            }`,
          );
        }

        // Construct the transaction event
        const siftEvent: ISiftEvent = {
          $type: '$transaction',
          $user_id: responseData?.emailAddress || orderData?.emailAddress,
          $session_id: sessionId,
          $transaction_id: responseData?.merchantTxnId || orderId,
          $order_id: orderId,
          $user_email: responseData?.emailAddress || orderData.emailAddress,
          $amount: this.convertToIntegerMicros(totalAmount), // Convert to integer micros
          $currency_code: responseData?.currency_code || 'USD',
          $transaction_type: '$sale',
          $transaction_status: '$success',
          // Add is_express flag for express payments
          ...(options?.isExpressPayment ? { is_express_no_ML: true } : {}),
          $payment_method: {
            $payment_type: paymentType,
            $payment_gateway: paymentGateway,
          },
        };

        if (orderData?.address1) {
          this.addAddressDetails(siftEvent, responseData, orderData);
        }

        // Add card details if this is a credit card payment
        if (paymentType === '$credit_card') {
          const cardBin = `${options?.cardInfo?.number}`.substring(0, 8);
          const cardLast4 = responseData?.cardLast4 || orderData?.cardLast4;
          if (window.DEBUG) {
            console.log(`[Sift Debug] Card: "${cardBin}", "${cardLast4}"`);
          }
          if (cardBin) {
            siftEvent.$payment_method.$card_bin = cardBin;
          }
          if (cardLast4) {
            siftEvent.$payment_method.$card_last4 = cardLast4;
          }
        }

        if (`${paymentGateway}`.toLocaleLowerCase().includes('paypal')) {
          siftEvent.$payment_method.$paypal_payer_id = orderData?.payerId;
        }

        this.addCustomParams(siftEvent, { ...orderData, ...responseData });

        return siftEvent;
      },
      { orderData, responseData },
      options?.returnScore,
    );

    // After creating the order, link the session to the user if we have both IDsAdd commentMore actions
    const sessionId =
      orderData?.custom_order_auid ||
      orderData?.sessionId ||
      orderData?.custom_order_sid;
    const userId = orderData?.emailAddress || responseData?.emailAddress;
    if (window.DEBUG) {
      console.log(
        `[Sift Debug] Trying to Linking session for order - ${
          responseData.orderId || orderData.orderId
        }`,
        { sessionId, userId },
      );
    }
    if (userId && sessionId && !skipSessionLink && options?.isExpressPayment) {
      if (window.DEBUG) {
        console.log(
          `[Sift Debug] Linking session ${sessionId} to user ${userId}`,
        );
      }
      await this.linkSessionToUser(userId, sessionId);
    }
  }

  static async updateOrder(
    originalOrderData: any,
    upsellData: any,
    responseData: any,
    options?: {
      paymentType?: string;
      paymentGateway?: string;
      returnScore?: boolean;
      cardInfo?: ICreditCardInfo;
    },
  ): Promise<void> {
    await this.safelyTrackEvent(
      '$update_order',
      'update_order',
      async () => {
        if (!originalOrderData || !upsellData || !responseData) {
          if (window.DEBUG)
            console.log(
              `[Sift Debug] updateOrder aborted - missing required data`,
            );
          return null;
        }

        // Verify we have the original order ID
        const orderId = originalOrderData.orderId;
        if (!orderId) return null;

        const sessionId =
          responseData?.customFields?.auid ||
          originalOrderData.sessionId ||
          originalOrderData.custom_order_sid;
        const userId = originalOrderData.emailAddress;

        // Extract the upsell product
        const product = {
          id: upsellData.product1_id,
          name: upsellData.product1_name || `Upsell Product`,
          price: this.convertToIntegerMicros(upsellData.product1_price),
          quantity: 1,
          currency_code: upsellData.currency_code || 'USD',
        };

        // Always apply the mapping for payment type
        const rawPaymentType = upsellData.paySource;

        // Map the payment type regardless of source
        const paymentType = this.mapPaymentType(rawPaymentType);

        // Get merchant ID from order data or response
        const merchantId =
          upsellData.forceMerchantId ||
          responseData.merchantId ||
          originalOrderData.merchantId ||
          responseData?.items[0]?.merchantId;

        // Use provided gateway or look up from merchant ID
        let paymentGateway = options?.paymentGateway;
        if (!paymentGateway && merchantId) {
          paymentGateway = await this.getGatewayFromMerchantId(
            merchantId,
            orderId,
          );
        }

        if (window.DEBUG) {
          console.log(`[Sift Debug] Update order payment info:`, {
            rawPaymentType,
            mappedPaymentType: paymentType,
            merchantId,
            paymentGateway,
          });
        }

        const items = Array.isArray(responseData?.items)
          ? responseData?.items?.map((item: any) => {
              return {
                $item_id: String(item.productId),
                $product_title: item.name,
                $price: this.convertToIntegerMicros(item.price),
                $quantity: 1,
                $currency_code: responseData?.currencyCode,
              };
            })
          : [
              {
                $item_id: String(product.id),
                $product_title: product.name,
                $price: product.price,
                $quantity: 1,
                $currency_code: product.currency_code,
              },
            ];

        // Construct the update order event
        const siftEvent: ISiftEvent = {
          $type: '$update_order',
          $user_email: responseData?.emailAddress,
          $user_id: userId || sessionId,
          $session_id: sessionId,
          $order_id: orderId,
          $amount: this.convertToIntegerMicros(responseData.totalAmount),
          $currency_code: product.currency_code,
          $items: items,
          crm_is_upsell_no_ML: true,
          $payment_methods: [
            {
              $payment_type: paymentType,
              $payment_gateway: paymentGateway,
            },
          ],
        };

        this.addAddressDetails(siftEvent, null, {
          ...originalOrderData,
          ...upsellData,
          ...responseData,
        });
        this.addCustomParams(siftEvent, {
          ...originalOrderData,
          ...upsellData,
          ...responseData,
        });

        return siftEvent;
      },
      { originalOrderData, upsellData, responseData },
      options?.returnScore,
    );
  }

  static async trackUpsellTransaction(
    orderData: any,
    responseData: any,
    options?: {
      paymentType?: string;
      paymentGateway?: string;
      returnScore?: boolean;
      isExpressPayment?: boolean;
    },
    skipSessionLink = false,
  ): Promise<void> {
    await this.safelyTrackEvent(
      '$transaction',
      'transaction',
      async () => {
        if (!orderData || !responseData) {
          if (window.DEBUG)
            console.log(
              `[Sift Debug] trackTransaction aborted - missing orderData or responseData`,
            );
          return null;
        }

        // Use either the response orderId or the original one
        const orderId = responseData.orderId || orderData.orderId;
        if (!orderId) return null;

        const sessionId =
          orderData.custom_order_auid ||
          orderData.sessionId ||
          orderData.custom_order_sid;
        const userId = orderData?.emailAddress || responseData?.emailAddress;

        // Extract products and calculate total amount
        const products = [];
        let totalAmount = 0;
        let i = 1;

        while (orderData[`product${i}_id`]) {
          const price = parseFloat(`${orderData[`product${i}_price`]}`) || 0;
          // const qty = parseInt(`${orderData[`product${i}_qty`]}`, 10) || 1;

          products.push({
            id: orderData[`product${i}_id`],
            price: this.convertToIntegerMicros(price),
            quantity: 1,
          });

          totalAmount += price;
          i++;
        }

        if (products.length === 0) return null;

        // Always apply the mapping - don't use raw value even if provided in options
        const rawPaymentType =
          orderData.paySource ||
          orderData.custom_order_paymentMethod ||
          options?.paymentType;

        // Map the payment type regardless of source to ensure consistent formatting
        const paymentType = this.mapPaymentType(rawPaymentType);

        if (window.DEBUG) {
          console.log(
            `[Sift Debug] Payment type mapping: ${
              rawPaymentType || 'unknown'
            } → ${paymentType}`,
          );
        }

        // Get merchant ID from order data or response
        const merchantId = responseData?.items?.[0]?.merchantId;

        // Use provided gateway option first, or look up from merchant ID
        let paymentGateway = options?.paymentGateway;

        // If no gateway provided in options, look it up from merchant ID
        if (!paymentGateway && merchantId) {
          paymentGateway = await this.getGatewayFromMerchantId(
            merchantId,
            orderId,
          );
        }

        if (window.DEBUG) {
          console.log(
            `[Sift Debug] Payment gateway: ${
              options?.paymentGateway
                ? `"${paymentGateway}" (provided in options)`
                : `"${paymentGateway}" from merchantId ${
                    merchantId || 'not provided'
                  }`
            }`,
          );
        }

        // Construct the transaction event
        const siftEvent: ISiftEvent = {
          $type: '$transaction',
          $user_id: userId,
          $session_id: sessionId,
          $transaction_id: responseData?.merchantTxnId || orderId,
          $order_id: orderId,
          $user_email: orderData.emailAddress,
          $amount: this.convertToIntegerMicros(totalAmount), // Convert to integer micros
          $currency_code: orderData.currency_code || 'USD',
          $transaction_type: '$sale',
          $transaction_status: '$success',
          // Add is_express flag for express payments
          ...(options?.isExpressPayment ? { is_express_no_ML: true } : {}),
          $payment_method: {
            $payment_type: paymentType,
            $payment_gateway: paymentGateway,
          },
        };

        if (orderData?.address1) {
          this.addAddressDetails(siftEvent, responseData, orderData);
        }

        // Add card details if this is a credit card payment
        if (paymentType === '$credit_card') {
          const cardBin = orderData?.cardBin || responseData?.cardBin;
          const cardLast4 =
            orderData?.cardLast4 || responseData?.last4 || orderData?.last4;

          if (cardBin) {
            siftEvent.$payment_method.$card_bin = cardBin;
          }
          if (cardLast4) {
            siftEvent.$payment_method.$card_last4 = cardLast4;
          }
        }

        if (`${paymentGateway}`.toLocaleLowerCase().includes('paypal')) {
          siftEvent.$payment_method.$paypal_payer_id = orderData?.payerId;
        }

        this.addCustomParams(siftEvent, { ...orderData, ...responseData });

        return siftEvent;
      },
      { orderData, responseData },
      options?.returnScore,
    );

    // After creating the order, link the session to the user if we have both IDsAdd commentMore actions
    // const sessionId = orderData?.custom_order_auid || orderData?.sessionId || orderData?.custom_order_sid;
    // const userId = orderData?.emailAddress || responseData?.emailAddress;
    // if (window.DEBUG) {
    //   console.log(`[Sift Debug] Trying to Linking session for order - ${responseData?.orderId || orderData?.orderId}`, { sessionId, userId });
    // }
    // if (userId && sessionId && !skipSessionLink) {
    //   if (window.DEBUG) {
    //     console.log(`[Sift Debug] Linking session ${sessionId} to user ${userId}`);
    //   }
    //   await this.linkSessionToUser(userId, sessionId);
    // }
  }

  static async trackFailedTransaction(
    orderId: string,
    orderData: any,
    options?: {
      paymentType?: string;
      paymentGateway?: string;
      returnScore?: boolean;
      isExpressPayment?: boolean;
      errorMessage?: string;
      cardInfo?: ICreditCardInfo;
    },
  ): Promise<void> {
    if (window.DEBUG) {
      console.log(
        `[Sift Debug] trackFailedTransaction called with: ` +
          JSON.stringify(
            {
              orderData,
              options,
            },
            null,
            2,
          ),
      );
    }
    await this.safelyTrackEvent(
      '$transaction',
      'transaction',
      async () => {
        if (orderId) {
          orderData = await Api.queryOrder(orderId);
        }

        const sessionId =
          orderData?.custom_order_auid ||
          orderData?.customFields?.auid ||
          orderData?.sessionId ||
          orderData?.customFields?.sid;
        const userId = orderData?.emailAddress;

        if (!sessionId) return null;

        // Extract products and calculate total amount
        const products = [];
        let totalAmount = 0;
        let i = 1;

        if (orderData?.products) {
          while (orderData?.products[i]?.productId) {
            const price = parseFloat(`${orderData?.products?.[0]?.price}`) || 0;
            const qty = parseInt(`${orderData?.products?.[0]?.qty}`, 10) || 1;

            const priceMicros = this.convertToIntegerMicros(price);

            products.push({
              id: orderData?.products?.[0]?.productId,
              price: priceMicros,
              quantity: 1,
            });

            totalAmount += price * qty;
            i++;
          }
        }

        const rawPaymentType = options?.paymentType || orderData?.paySource;
        const paymentType = this.mapPaymentType(rawPaymentType);

        if (window.DEBUG) {
          console.log(
            `[Sift Debug] Payment type mapping: ${
              rawPaymentType || 'unknown'
            } → ${paymentType}`,
          );
        }

        const merchantId =
          orderData?.forceMerchantId ||
          orderData?.merchantId ||
          orderData?.paypalBillerId ||
          orderData?.products?.[0]?.merchantId;
        let paymentGateway = options?.paymentGateway;
        if (!paymentGateway && merchantId) {
          paymentGateway = await this.getGatewayFromMerchantId(
            merchantId,
            orderId,
          );
        }

        if (window.DEBUG) {
          console.log(
            `[Sift Debug] Payment gateway: ${
              options?.paymentGateway
                ? `"${paymentGateway}" (provided in options)`
                : `"${paymentGateway}" from merchantId ${
                    merchantId || 'not provided'
                  }`
            }`,
          );
        }

        // Construct the transaction event
        const siftEvent: ISiftEvent = {
          $type: '$transaction',
          $user_id: userId || sessionId,
          $session_id: sessionId,
          $transaction_id: orderId,
          $order_id: orderId,
          $amount: this.convertToIntegerMicros(totalAmount),
          $currency_code: orderData.currency_code || 'USD',
          $transaction_type: '$sale',
          $transaction_status: '$failure',
          ...(options?.isExpressPayment ? { is_express_no_ML: true } : {}),
          $decline_category: '$other',
          $payment_method: {
            $payment_type: paymentType,
            $payment_gateway: paymentGateway,
          },
          crm_decline_message: options?.errorMessage,
        };

        if (orderData.emailAddress) {
          siftEvent.$user_email = orderData.emailAddress;
        }

        if (orderData?.address1) {
          this.addAddressDetails(siftEvent, null, orderData);
        }

        // Add card details if this is a credit card payment
        if (paymentType === '$credit_card') {
          const cardNumber = `${options?.cardInfo?.number}`;
          const cardBin = cardNumber.substring(0, 8);
          const cardLast4 = cardNumber.substring(
            cardNumber.length - 4,
            cardNumber.length,
          );
          if (window.DEBUG) {
            console.log(`[Sift Debug] Card: "${cardBin}", "${cardLast4}"`);
          }
          if (cardBin) {
            siftEvent.$payment_method.$card_bin = cardBin;
          }
          if (cardLast4) {
            siftEvent.$payment_method.$card_last4 = cardLast4;
          }
        }

        if (`${paymentGateway}`.toLocaleLowerCase().includes('paypal')) {
          siftEvent.$payment_method.$paypal_payer_id = orderData?.payerId;
        }

        this.addCustomParams(siftEvent, orderData);

        return siftEvent;
      },
      {},
      options?.returnScore,
    );
  }

  static async linkSessionToUser(
    userId: string,
    sessionId: string,
  ): Promise<void> {
    await this.safelyTrackEvent(
      '$link_session_to_user',
      'link_session_to_user',
      () => {
        if (!userId || !sessionId) return null;

        return {
          $type: '$link_session_to_user',
          $user_id: this.encodeUserId(userId),
          $session_id: sessionId,
        };
      },
      { userId, sessionId },
    );
  }
}
